/**
 * Data model ported from rnc_platform.html design prototype.
 */

export const CHANNELS = [
  { key: "rnc", name: "RNC", abbr: "RNC", color: "#4F46E5" },
  { key: "custom", name: "ABM List", abbr: "ABM", color: "#0F766E" },
  { key: "linkedin", name: "LinkedIn", abbr: "in", color: "#0A66C2" },
  { key: "quora", name: "Quora", abbr: "Q", color: "#A82400" },
  { key: "reddit", name: "Reddit", abbr: "rd", color: "#FF4500" },
  { key: "zillow", name: "Zillow", abbr: "Z", color: "#3B82F6" },
  { key: "realtor", name: "Realtor.com", abbr: "R", color: "#F97316" },
  { key: "redfin", name: "Redfin", abbr: "Rf", color: "#A82222" },
  { key: "website", name: "Website", abbr: "W", color: "#6366F1" },
  { key: "facebook", name: "Facebook", abbr: "f", color: "#1877F2" },
  { key: "instagram", name: "Instagram", abbr: "ig", color: "#E1306C" },
  { key: "googleads", name: "Google Ads", abbr: "G", color: "#34A853" },
  { key: "x", name: "X", abbr: "X", color: "#0F1419" },
];

const CHANNEL_ALIASES = {
  google_ads: "googleads",
  "google-ads": "googleads",
  twitter: "x",
  gmail: "email",
};

/** Resolve channel metadata. Never invent a default channel (e.g. Quora/LinkedIn). */
export function chInfo(key) {
  if (key == null || key === "") {
    return { key: "", name: "N/A", abbr: "—", color: "#94A3B8" };
  }
  const normalized = CHANNEL_ALIASES[String(key)] || key;
  const found = CHANNELS.find((c) => c.key === normalized);
  if (found) return found;
  const label = String(key);
  return {
    key: label,
    name: label,
    abbr: label.slice(0, 3).toUpperCase(),
    color: "#64748B",
  };
}

export const CAMPAIGNS = [
  { id: "CMP-01", name: "Real Estate Outreach", owner: "Maya", channels: ["zillow", "realtor", "redfin"], workflowId: "WF-01", version: "v1.4", status: "Active", lastActivity: "8m ago", isDefault: true },
  { id: "CMP-02", name: "LinkedIn Enterprise Outreach", owner: "Sarah Chen", channels: ["linkedin"], workflowId: "WF-02", version: "v2.1", status: "Active", lastActivity: "21m ago", isDefault: true },
  { id: "CMP-03", name: "Quora Authority Outreach", owner: "Diego Ramirez", channels: ["quora"], workflowId: "WF-03", version: "v1.0", status: "Active", lastActivity: "1h ago", isDefault: true },
  { id: "CMP-04", name: "Reddit Community Outreach", owner: "Linh Tran", channels: ["reddit"], workflowId: "WF-04", version: "v1.2", status: "Paused", lastActivity: "3h ago", isDefault: true },
  { id: "CMP-05", name: "Website Inbound Campaign", owner: "Marcus Bauer", channels: ["website"], workflowId: "WF-05", version: "v1.1", status: "Active", lastActivity: "40m ago", isDefault: true },
];

export function getCampaign(id) {
  if (id == null || id === "") return null;
  return CAMPAIGNS.find((c) => c.id === id) || null;
}

/**
 * Resolve a workflow definition by id.
 * Live RNC projects from `bnc.projects` have no workflow assignment — callers
 * must treat `assigned: false` as a normal state, not an error.
 */
export function resolveWorkflow(workflowId) {
  if (workflowId == null || workflowId === "") {
    return {
      workflow: null,
      assigned: false,
      reason: "not_assigned",
    };
  }
  const workflow = WORKFLOWS[workflowId] || null;
  if (!workflow) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[signals.workflow] missing workflow definition", { workflowId });
    }
    return {
      workflow: null,
      assigned: false,
      reason: "definition_missing",
    };
  }
  if (!Array.isArray(workflow.steps)) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[signals.workflow] workflow missing steps array", { workflowId });
    }
    return {
      workflow: { ...workflow, steps: [] },
      assigned: true,
      reason: "steps_missing",
    };
  }
  return { workflow, assigned: true, reason: null };
}

function processWorkflowStep(s, out) {
  if (!s || typeof s !== "object") return;
  if (s.kind === "condition") {
    out.push({ kind: "condition", title: s.title || "If / Else", id: s.id, badge: s.badge });
    const branches = Array.isArray(s.branches) ? s.branches : [];
    for (const branch of branches) {
      if (!branch?.node || typeof branch.node !== "object") continue;
      processWorkflowStep(
        { ...branch.node, branchLabel: branch.label || branch.node.branchLabel || "" },
        out,
      );
    }
    return;
  }
  out.push(s);
}

/**
 * Flatten a workflow tree for list/timeline UI.
 * Never throws — invalid/missing workflows return [].
 */
export function flattenWorkflow(wf) {
  if (wf == null || typeof wf !== "object") {
    return [];
  }
  const steps = Array.isArray(wf.steps) ? wf.steps : null;
  if (!steps) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[signals.workflow] flattenWorkflow: missing steps", {
        workflowId: wf.id || null,
      });
    }
    return [];
  }

  const out = [];
  for (const s of steps) {
    processWorkflowStep(s, out);
  }
  return out;
}

export const WORKFLOWS = {
  "WF-01": {
    id: "WF-01",
    name: "Real Estate Outreach",
    steps: [
      { kind: "action", badge: "EMAIL", title: "Introductory Email", detail: "Send intro + portfolio pricing" },
      { kind: "wait", title: "Wait 2 Days" },
      { kind: "action", badge: "EMAIL", title: "Follow-up Email", detail: "Pattern interrupt follow-up" },
      { kind: "wait", title: "Wait 2 Days" },
      { kind: "action", badge: "WHATSAPP", title: "WhatsApp Message", detail: "Channel switch nudge" },
      { kind: "wait", title: "Wait 2 Days" },
      { kind: "action", badge: "AI CALL", title: "AI Phone Call", detail: "Qualifying voice call" },
      { kind: "goal", title: "Qualified or Unqualified" },
    ],
  },
  "WF-02": {
    id: "WF-02",
    name: "LinkedIn Enterprise Outreach",
    steps: [
      { kind: "action", badge: "LINKEDIN", title: "LinkedIn Connect", detail: "Send connection request" },
      { kind: "wait", title: "Wait 2 Days" },
      {
        kind: "condition",
        title: "If accepted?",
        branches: [
          { label: "Yes", node: { kind: "action", badge: "LINKEDIN", title: "LinkedIn — Message", detail: "{{account.name}} intro message" } },
          { label: "No", node: { kind: "action", badge: "EMAIL", title: "Email — Pattern Interrupt", detail: "{{account.name}} → faster GTM via AI?" } },
        ],
      },
      { kind: "wait", title: "Wait 3 Days" },
      {
        kind: "condition",
        title: "If no reply?",
        branches: [
          { label: "Yes", node: { kind: "action", badge: "EMAIL", title: "Email — Value angle #2", detail: "How {{account.name}} could close 12% more pipeline" } },
        ],
      },
      { kind: "wait", title: "Wait 2 Days" },
      { kind: "goal", title: "Qualified or Unqualified" },
    ],
  },
  "WF-03": {
    id: "WF-03",
    name: "Quora Authority Outreach",
    steps: [
      { kind: "action", badge: "COMMENT", title: "AI Answer Reply", detail: "Reply with expert answer + soft CTA" },
      { kind: "wait", title: "Wait 1 Day" },
      { kind: "action", badge: "DM", title: "Quora DM", detail: "Follow-up direct message" },
      { kind: "goal", title: "Qualified or Unqualified" },
    ],
  },
  "WF-04": {
    id: "WF-04",
    name: "Reddit Community Outreach",
    steps: [
      { kind: "action", badge: "COMMENT", title: "AI Comment Reply", detail: "Reply on relevant thread" },
      { kind: "wait", title: "Wait 1 Day" },
      { kind: "action", badge: "DM", title: "Reddit DM", detail: "Soft outreach message" },
      { kind: "goal", title: "Qualified or Unqualified" },
    ],
  },
  "WF-05": {
    id: "WF-05",
    name: "Website Inbound Campaign",
    steps: [
      { kind: "action", badge: "EMAIL", title: "Instant Reply Email", detail: "Auto-response to form fill" },
      { kind: "wait", title: "Wait 1 Day" },
      { kind: "action", badge: "AI CALL", title: "AI Phone Call", detail: "Qualify inbound request" },
      { kind: "goal", title: "Qualified or Unqualified" },
    ],
  },
};

/** Mutable RNC records (SQL/CRM flags update in UI) */
export const RNCS = [
  {
    id: "RNC-1001", company: "Harbor & Main Realty", initials: "HM", color: "#4F46E5", type: "Commercial", units: "40 units",
    contact: "Daniel Reyes", email: "daniel@harbormain.com", phone: "+1 (415) 552-0148",
    channel: "zillow", campaignId: "CMP-01", workflowId: "WF-01", workflowVersion: "v1.4",
    assignedDate: "June 23, 2026 · 10:00 AM", stepIndex: 3, currentStep: "Follow-up Email Sent", nextStep: "WhatsApp Message", remainingTime: "~2 days",
    status: "In Progress", progress: 60, lastActivity: "2 hrs ago", dateAdded: "June 23, 2026 · 10:00 AM",
    notes: "Listed 3 commercial units in SoMa district. Inquired about portfolio management pricing via Zillow contact form. Looking to consolidate from 2 other agencies.",
  },
  {
    id: "RNC-1002", company: "Skyline Ridge Properties", initials: "SR", color: "#16A34A", type: "Residential", units: "12 units",
    contact: "Priya Nair", email: "priya@skylineridge.io", phone: "+1 (628) 330-9921",
    channel: "realtor", campaignId: "CMP-01", workflowId: "WF-01", workflowVersion: "v1.2",
    assignedDate: "June 18, 2026 · 9:05 AM", stepIndex: 7,
    status: "Qualified", progress: 100, lastActivity: "Yesterday", dateAdded: "June 18, 2026", qualifiedOn: "June 27, 2026 · 11:42 AM",
    summary: "Priya responded to the WhatsApp follow-up within 20 minutes, confirming Skyline Ridge is actively comparing property management partners after a contract ending with their current vendor next month. She asked about pricing tiers for a 12-unit residential portfolio and availability for a call this week. Tone was warm and direct — no objections raised, budget appears pre-approved by leadership.",
    recommendation: "High-intent lead with budget authority and a clear timeline. Maya recommends transferring to ALLCAD immediately for a sales call before the current-vendor contract lapses — interest is likely to cool after this week.",
    handoff: "Handed off Jun 27, 11:58 AM · Assigned to ALLCAD Sales Queue", sqlConverted: false, crmPushed: false, crmName: null,
  },
  {
    id: "RNC-1003", company: "Vantage Point Estates", initials: "VP", color: "#DC2626", type: "Residential", units: "6 units",
    contact: "Marcus Webb", email: "m.webb@vantagepoint.com", phone: "+1 (213) 487-6630",
    channel: "redfin", campaignId: "CMP-01", workflowId: "WF-01", workflowVersion: "v1.1",
    assignedDate: "June 12, 2026 · 8:40 AM", stepIndex: 7,
    status: "Unqualified", progress: 100, lastActivity: "3 days ago", dateAdded: "June 12, 2026", closedOn: "June 22, 2026 · 4:30 PM",
    reasons: ["No Response", "Number Not on WhatsApp", "Call Not Answered"],
  },
  {
    id: "RNC-1004", company: "Greenfield Capital Holdings", initials: "GC", color: "#3B82F6", type: "Mixed-use", units: "28 units",
    contact: "Elena Suarez", email: "elena@greenfieldcap.com", phone: "+1 (917) 204-5587",
    channel: "zillow", campaignId: "CMP-01", workflowId: "WF-01", workflowVersion: "v1.4",
    assignedDate: "June 24, 2026 · 9:10 AM", stepIndex: 5, currentStep: "WhatsApp Message Sent", nextStep: "AI Phone Call", remainingTime: "~1 day",
    status: "WhatsApp Sent", progress: 80, lastActivity: "5 hrs ago", dateAdded: "June 24, 2026 · 9:10 AM",
    notes: "Mixed-use portfolio inquiry via Zillow. No response to email outreach; WhatsApp follow-up sent and awaiting reply.",
  },
  {
    id: "RNC-1005", company: "North Shore Living", initials: "NS", color: "#14B8A6", type: "Residential", units: "9 units",
    contact: "Tariq Hassan", email: "tariq@northshoreliving.com", phone: "+1 (646) 778-2204",
    channel: "realtor", campaignId: "CMP-01", workflowId: "WF-01", workflowVersion: "v1.4",
    assignedDate: "July 1, 2026 · 3:40 PM", stepIndex: 1, currentStep: "Introductory Email Sent", nextStep: "Follow-up Email", remainingTime: "~2 days",
    status: "Email Sent", progress: 25, lastActivity: "1 hr ago", dateAdded: "July 1, 2026 · 3:40 PM",
    notes: "Fresh inquiry from Realtor.com listing page. Introductory email sent, awaiting delivery confirmation.",
  },
  {
    id: "RNC-1006", company: "Bayview Crest Realty", initials: "BC", color: "#F97316", type: "Residential", units: "18 units",
    contact: "Sofia Martinez", email: "sofia@bayviewcrest.com", phone: "+1 (305) 660-1145",
    channel: "redfin", campaignId: "CMP-01", workflowId: "WF-01", workflowVersion: "v1.4",
    assignedDate: "June 20, 2026 · 1:15 PM", stepIndex: 6, currentStep: "AI Phone Call", nextStep: "Qualification Decision", remainingTime: "In progress now",
    status: "AI Call in Progress", progress: 90, lastActivity: "12 min ago", dateAdded: "June 20, 2026 · 1:15 PM",
    notes: "Engaged across email and WhatsApp. Maya is currently placing the qualifying AI phone call.",
  },
  {
    id: "RNC-1007", company: "Parkline Residences", initials: "PL", color: "#16A34A", type: "Residential", units: "22 units",
    contact: "James Okafor", email: "james@parklineres.com", phone: "+1 (312) 905-7763",
    channel: "zillow", campaignId: "CMP-01", workflowId: "WF-01", workflowVersion: "v1.4",
    assignedDate: "June 15, 2026 · 11:00 AM", stepIndex: 7,
    status: "Qualified", progress: 100, lastActivity: "2 days ago", dateAdded: "June 15, 2026", qualifiedOn: "June 29, 2026 · 3:05 PM",
    summary: "James confirmed via the AI phone call that Parkline Residences is finalizing budget for Q3 and wants a proposal for their 22-unit residential portfolio. He requested a call with a specialist to review onboarding timelines.",
    recommendation: "Qualified with confirmed budget and a Q3 timeline. Maya recommends an immediate handoff to ALLCAD so a specialist can lock in the proposal call.",
    handoff: "Handed off Jun 29, 3:20 PM · Assigned to ALLCAD Sales Queue", sqlConverted: false, crmPushed: false, crmName: null,
  },
  {
    id: "RNC-1008", company: "Nova Logistics Inc.", initials: "NL", color: "#0A66C2", type: "Enterprise", units: "500+ employees",
    contact: "Alex Whitfield", email: "alex.whitfield@novalogistics.com", phone: "+1 (312) 555-0199",
    channel: "linkedin", campaignId: "CMP-02", workflowId: "WF-02", workflowVersion: "v2.1",
    assignedDate: "June 28, 2026 · 2:00 PM", stepIndex: 4, currentStep: "LinkedIn — Message Sent", nextStep: "Email — Value angle #2 (if no reply)", remainingTime: "~3 days",
    status: "In Progress", progress: 45, lastActivity: "6 hrs ago", dateAdded: "June 28, 2026 · 2:00 PM",
    notes: "Connection accepted within a few hours. LinkedIn intro message sent referencing recent Series C announcement.",
  },
  {
    id: "RNC-1009", company: "Bright Path Realty Group", initials: "BP", color: "#7C3AED", type: "Residential", units: "15 units",
    contact: "Renee Ackerman", email: "renee@brightpathrealty.com", phone: "+1 (503) 555-0148",
    channel: "quora", campaignId: "CMP-03", workflowId: "WF-03", workflowVersion: "v1.0",
    assignedDate: "June 29, 2026 · 10:20 AM", stepIndex: 3,
    status: "Qualified", progress: 100, lastActivity: "Today", dateAdded: "June 29, 2026", qualifiedOn: "June 30, 2026 · 4:15 PM",
    summary: "Renee replied to Maya's Quora DM after engaging with an answer about property management software ROI. She confirmed Bright Path is evaluating vendors this quarter for a 15-unit residential portfolio and asked for a pricing walkthrough.",
    recommendation: "Warm inbound intent from an organic content engagement. Maya recommends fast handoff — Quora-sourced leads convert best within 48 hours of first reply.",
    handoff: "Handed off Jun 30, 4:20 PM · Assigned to ALLCAD Sales Queue", sqlConverted: false, crmPushed: false, crmName: null,
  },
];

export function getRNC(id) {
  return RNCS.find((r) => r.id === id);
}

export function statusColor(s) {
  const key = String(s || "");
  if (key === "In Progress" || key === "WhatsApp Sent" || key === "AI Call in Progress") {
    return "amber";
  }
  if (key === "Email Sent") return "gray";
  if (key.startsWith("Qualified") || key === "Qualified") return "green";
  if (key.startsWith("Unqualified") || key === "Unqualified") return "red";
  return "gray";
}

export const CRM_OPTIONS = [
  { key: "allcad", name: "ALLCAD", color: "#4F46E5" },
  { key: "salesforce", name: "Salesforce", color: "#00A1E0" },
  { key: "hubspot", name: "HubSpot", color: "#FF7A59" },
  { key: "dynamics", name: "Microsoft Dynamics", color: "#0078D4" },
];
